import { Head, router } from "@inertiajs/react"
import { ArrowLeft, Check, Star } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import AppLayout from "@/layouts/app-layout"
import { coffeesPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"

interface Sample {
  id: number
  name: string
  origin: string | null
  producer: string | null
  variety: string | null
  process: string | null
}

interface UserSampleOption {
  id: number
  total_score: number
  sample: Sample
  user_name: string | null
}

interface CuppingOption {
  id: number
  title: string
  cupping_form: string
  created_at: string
  user_samples: UserSampleOption[]
}

interface Props {
  cuppings: CuppingOption[]
}

export default function CoffeesNew({ cuppings }: Props) {
  const { t } = useTranslation()
  const [name, setName] = useState("")
  const [selectedCupping, setSelectedCupping] = useState<CuppingOption | null>(
    null,
  )
  const [selectedUserSample, setSelectedUserSample] =
    useState<UserSampleOption | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: t("coffees.breadcrumb", "My Coffees"), href: coffeesPath() },
    { title: t("coffees.new_coffee", "New Coffee Page"), href: "#" },
  ]

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name || !selectedCupping || !selectedUserSample) return

    setSubmitting(true)
    router.post(coffeesPath(), {
      coffee: {
        name,
        pinned_cupping_id: selectedCupping.id,
        pinned_user_sample_id: selectedUserSample.id,
      },
    })
  }

  const handleSelectCupping = (cupping: CuppingOption) => {
    setSelectedCupping(cupping)
    setSelectedUserSample(null)
    if (!name && cupping.user_samples.length > 0) {
      setName(cupping.user_samples[0].sample.name || cupping.title)
    }
  }

  const handleSelectUserSample = (us: UserSampleOption) => {
    setSelectedUserSample(us)
    if (!name) {
      setName(us.sample.name || "")
    }
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("coffees.new_coffee", "New Coffee Page")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => router.visit(coffeesPath())}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {t("coffees.new_coffee", "New Coffee Page")}
            </h1>
            <p className="text-muted-foreground text-sm">
              {t(
                "coffees.new_description",
                "Select a cupping session to create a product page from.",
              )}
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="mx-auto w-full max-w-3xl space-y-6"
        >
          {/* Step 1: Name */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("coffees.step_name", "Coffee Name")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Label htmlFor="name">
                {t("coffees.name_label", "Name for the product page")}
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t(
                  "coffees.name_placeholder",
                  "e.g. Ethiopia Yirgacheffe Natural",
                )}
                className="mt-1.5"
              />
            </CardContent>
          </Card>

          {/* Step 2: Select Cupping */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                {t("coffees.step_cupping", "Select Cupping Session")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cuppings.length === 0 ? (
                <p className="text-muted-foreground py-4 text-center text-sm">
                  {t(
                    "coffees.no_cuppings",
                    "No cupping sessions with evaluations found. Complete a cupping first.",
                  )}
                </p>
              ) : (
                <div className="space-y-2">
                  {cuppings.map((cupping) => (
                    <button
                      key={cupping.id}
                      type="button"
                      onClick={() => handleSelectCupping(cupping)}
                      className={`w-full rounded-lg border p-3 text-left transition-colors ${
                        selectedCupping?.id === cupping.id
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{cupping.title}</p>
                          <p className="text-muted-foreground text-xs">
                            {cupping.cupping_form.toUpperCase()} ·{" "}
                            {cupping.user_samples.length}{" "}
                            {t("coffees.evaluations", "evaluations")}
                          </p>
                        </div>
                        {selectedCupping?.id === cupping.id && (
                          <Check className="text-primary h-5 w-5" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Step 3: Select Golden Session */}
          {selectedCupping && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">
                  {t("coffees.step_golden", "Select Golden Session")}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-3 text-sm">
                  {t(
                    "coffees.golden_description",
                    "Choose the evaluation that best represents this coffee. Its scores and notes will populate the product page.",
                  )}
                </p>
                <div className="space-y-2">
                  {selectedCupping.user_samples.map((us) => (
                    <button
                      key={us.id}
                      type="button"
                      onClick={() => handleSelectUserSample(us)}
                      className={`w-full rounded-lg border p-3 text-left transition-colors ${
                        selectedUserSample?.id === us.id
                          ? "border-primary bg-primary/5"
                          : "hover:bg-muted/50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{us.sample.name}</p>
                          <p className="text-muted-foreground text-xs">
                            {us.sample.origin}
                            {us.user_name &&
                              ` · ${t("coffees.cupped_by", "Cupped by")} ${us.user_name}`}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="font-mono">
                            <Star className="mr-1 h-3 w-3" />
                            {us.total_score}
                          </Badge>
                          {selectedUserSample?.id === us.id && (
                            <Check className="text-primary h-5 w-5" />
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Submit */}
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={!name || !selectedUserSample || submitting}
              size="lg"
            >
              {submitting
                ? t("common.creating", "Creating...")
                : t("coffees.create_page", "Create Coffee Page")}
            </Button>
          </div>
        </form>
      </div>
    </AppLayout>
  )
}
